import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaChartBar, FaCheckCircle, FaBuilding, FaBolt, FaMapMarkerAlt, FaCity, FaGlobeAfrica, FaLightbulb, FaAward, FaHandshake, FaUsers, FaBriefcase, FaUmbrellaBeach, FaLandmark, FaSeedling, FaHome } from 'react-icons/fa';
import { motion } from 'framer-motion';
import CertificationsSection from '../components/CertificationsSection';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [countersStarted, setCountersStarted] = useState(false);

  // Counter hook for animating numbers
  const useCounter = (end, duration = 2000, start = 0) => {
    const [count, setCount] = useState(start);

    useEffect(() => {
      if (!countersStarted) return;

      let startTime = null;
      const startValue = start;
      const endValue = end;

      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);

        const easeOutQuad = (t) => t * (2 - t); // Easing function
        const currentCount = Math.floor(startValue + (endValue - startValue) * easeOutQuad(progress));

        setCount(currentCount);

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countersStarted, end, duration, start]);

    return count;
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 60 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  // Beautiful professional images for consulting
  const slides = [
    {
      id: 1,
      title: "Africa Rising",
      image: "https://images.unsplash.com/photo-1547036967-23d11aacaee0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
      description: "Empowering growth across the African continent"
    },
    {
      id: 2,
      title: "Strategic Planning",
      image: "/imagebg.jpg",
      description: "Professional consulting for strategic business growth"
    },
    {
      id: 3,
      title: "Team Collaboration",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80",
      description: "Expert guidance through collaborative partnerships"
    }
  ];

  // Initialize counters
  const experienceCount = useCounter(5, 2000);
  const projectsCount = useCounter(50, 2000);
  const officesCount = useCounter(2, 2000);
  const servicesCount = useCounter(16, 2000);

  // Auto-advance slider
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div>
      {/* Full-Screen Hero Section */}
      <section className="relative h-screen flex items-center bg-white dark:bg-gray-900 overflow-hidden -mt-16 lg:-mt-20">
        {/* Image Slider */}
        <div className="absolute inset-0">
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-30' : 'opacity-0'
              }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-white/60 dark:bg-gray-900/60"></div>
            </div>
          ))}
        </div>

        {/* Slider Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/80 dark:bg-gray-800/80 rounded-full flex items-center justify-center text-gray-800 dark:text-white hover:bg-white dark:hover:bg-gray-700 transition-all duration-300"
        >
          ←
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/80 dark:bg-gray-800/80 rounded-full flex items-center justify-center text-gray-800 dark:text-white hover:bg-white dark:hover:bg-gray-700 transition-all duration-300"
        >
          →
        </button>

        {/* Slider Dots */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-3">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-primary-600 scale-125'
                  : 'bg-white/50 dark:bg-gray-400/50 hover:bg-white/80 dark:hover:bg-gray-300/80'
              }`}
            />
          ))}
        </div>

        {/* Hero Content */}
        <motion.div
          className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          {/* Main Heading */}
          <motion.h1
            className="text-5xl sm:text-6xl lg:text-7xl font-extrabold mb-8 leading-tight"
            variants={fadeInUp}
          >
            <span className="block text-navy-900 dark:text-white">Trends</span>
            <span className="block bg-gradient-to-r from-primary-600 to-secondary-500 bg-clip-text text-transparent">Dynamiques</span>
            <span className="block text-lg lg:text-xl font-medium tracking-widest uppercase text-gray-600 dark:text-gray-400 mt-4">
              Consulting Limited
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            className="text-xl sm:text-2xl lg:text-3xl font-light mb-8 text-gray-700 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed"
            variants={fadeInUp}
          >
            Your partner in strategy, Growth and Impact
          </motion.p>


          {/* Action Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
            variants={fadeInUp}
          >
            <Link
              to="/services"
              className="bg-gradient-to-r from-primary-600 to-secondary-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Explore Our Services
            </Link>
            <Link
              to="/contact"
              className="bg-transparent border-2 border-navy-900 text-navy-900 dark:border-white dark:text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-navy-900 hover:text-white dark:hover:bg-white dark:hover:text-gray-900 transition-all duration-300"
            >
              Get In Touch
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-primary-100 dark:bg-primary-900 rounded-full opacity-10 -translate-x-32 -translate-y-32"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-secondary-100 dark:bg-secondary-900 rounded-full opacity-10 translate-x-32 translate-y-32"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <div className="inline-block mb-4">
              <span className="bg-gradient-to-r from-primary-600 to-secondary-500 text-white px-6 py-2 rounded-full text-sm font-semibold tracking-wide uppercase">
                Our Advantage
              </span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-navy-900 dark:text-white">
              Why Choose <span className="bg-gradient-to-r from-primary-600 to-secondary-500 bg-clip-text text-transparent">Trends Dynamiques</span>?
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              We combine local expertise with global standards to deliver exceptional consulting solutions across Tanzania and East Africa
            </p>
          </motion.div>

          {/* Features Grid */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={staggerContainer}
          >
            {[
              {
                icon: FaGlobeAfrica,
                number: "01",
                title: "Local Knowledge, Global Standards",
                description: "We blend deep understanding of the Tanzanian context with internationally recognized consulting practices, ensuring solutions that are both culturally relevant and world-class.",
                color: "from-blue-500 to-blue-600",
                bgColor: "bg-blue-50 dark:bg-blue-900/20"
              },
              {
                icon: FaLightbulb,
                number: "02",
                title: "Cross-Sector Expertise",
                description: "From non-profits to governments, tourism to energy, agriculture to real estate—we bring multidisciplinary solutions that deliver both social impact and sustainable profitability.",
                color: "from-orange-500 to-orange-600",
                bgColor: "bg-orange-50 dark:bg-orange-900/20"
              },
              {
                icon: FaAward,
                number: "03",
                title: "Proven Track Record",
                description: "With 50+ years of combined expertise, our team has successfully supported governments, development agencies, investors, and businesses across Africa with 100+ completed projects.",
                color: "from-green-500 to-green-600",
                bgColor: "bg-green-50 dark:bg-green-900/20"
              },
              {
                icon: FaHandshake,
                number: "04",
                title: "Impact-Oriented Partnership",
                description: "We go beyond delivering solutions to ensure lasting results through collaborative partnerships that contribute to sustainable growth, community resilience, and long-term success.",
                color: "from-purple-500 to-purple-600",
                bgColor: "bg-purple-50 dark:bg-purple-900/20"
              }
            ].map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
                  variants={scaleIn}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                >
                  {/* Gradient border effect */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>
                  <div className="absolute inset-[2px] bg-white dark:bg-gray-800 rounded-2xl"></div>

                  {/* Content */}
                  <div className="relative p-8 lg:p-10">
                    {/* Number Badge */}
                    <div className="absolute top-6 right-6 text-6xl font-bold text-gray-100 dark:text-gray-700 group-hover:text-gray-200 dark:group-hover:text-gray-600 transition-colors duration-500">
                      {feature.number}
                    </div>

                    {/* Icon */}
                    <div className={`inline-flex items-center justify-center w-16 h-16 rounded-xl ${feature.bgColor} mb-6 group-hover:scale-110 transition-transform duration-500`}>
                      <IconComponent className={`text-3xl bg-gradient-to-r ${feature.color} bg-clip-text text-transparent`} />
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl font-bold mb-4 text-navy-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-secondary-400 transition-colors duration-500">
                      {feature.title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-base">
                      {feature.description}
                    </p>

                    {/* Decorative line */}
                    <div className="mt-6 h-1 w-0 group-hover:w-20 bg-gradient-to-r from-primary-600 to-secondary-500 transition-all duration-500 rounded-full"></div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Bottom CTA */}
          <div className="text-center mt-16">
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
              Ready to experience the difference?
            </p>
            <Link
              to="/about"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-600 to-secondary-500 text-white rounded-full font-semibold text-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              Learn More About Us
              <span className="ml-2">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Trusted By Section - Customer Logos Slider */}
      <section className="py-12 lg:py-16 bg-gray-50 dark:bg-gray-800 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl lg:text-3xl font-bold text-center mb-8 text-navy-900 dark:text-secondary-500">
            Trusted By Leading Organizations
          </h2>
          <div className="relative">
            {/* Gradient overlays for fade effect */}
            <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-gray-50 dark:from-gray-800 to-transparent z-10"></div>
            <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-gray-50 dark:from-gray-800 to-transparent z-10"></div>

            {/* Scrolling container */}
            <div className="flex animate-scroll">
              {/* First set of logos */}
              <div className="flex items-center space-x-12 lg:space-x-16 min-w-max pr-12 lg:pr-16">
                {[
                  { name: 'UNDP', image: '/customers/undp.png' },
                  { name: 'UNIDO', image: '/customers/unido.png' },
                  { name: 'European Union', image: '/customers/eu1.jpg' },
                  { name: 'BioVision', image: '/customers/biovision.jpg' },
                  { name: 'Zanzibar Startup', image: '/customers/zanzibarstartup.png' },
                  { name: 'UNDP', image: '/customers/undp2.png' },
                  { name: 'European Union', image: '/customers/eu2.svg.png' },
                  { name: 'Practical Action', image: '/customers/practicla.jpeg' }
                ].map((customer, index) => (
                  <div
                    key={`set1-${index}`}
                    className="flex-shrink-0 w-32 h-20 lg:w-40 lg:h-24 flex items-center justify-center bg-white dark:bg-gray-700 rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow duration-300"
                  >
                    <img
                      src={customer.image}
                      alt={customer.name}
                      className="max-w-full max-h-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                    />
                  </div>
                ))}
              </div>

              {/* Duplicate set for seamless loop */}
              <div className="flex items-center space-x-12 lg:space-x-16 min-w-max pr-12 lg:pr-16">
                {[
                  { name: 'UNDP', image: '/customers/undp.png' },
                  { name: 'UNIDO', image: '/customers/unido.png' },
                  { name: 'European Union', image: '/customers/eu1.jpg' },
                  { name: 'BioVision', image: '/customers/biovision.jpg' },
                  { name: 'Zanzibar Startup', image: '/customers/zanzibarstartup.png' },
                  { name: 'UNDP', image: '/customers/undp2.png' },
                  { name: 'European Union', image: '/customers/eu2.svg.png' },
                  { name: 'Practical Action', image: '/customers/practicla.jpeg' }
                ].map((customer, index) => (
                  <div
                    key={`set2-${index}`}
                    className="flex-shrink-0 w-32 h-20 lg:w-40 lg:h-24 flex items-center justify-center bg-white dark:bg-gray-700 rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow duration-300"
                  >
                    <img
                      src={customer.image}
                      alt={customer.name}
                      className="max-w-full max-h-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 lg:py-24 bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">Our Impact in Numbers</h2>
            <p className="text-xl text-gray-200">Delivering measurable results across Tanzania and beyond</p>
          </motion.div>
          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            onViewportEnter={() => setCountersStarted(true)}
            variants={staggerContainer}
          >
            {[
              { number: experienceCount, suffix: '+', label: 'Years Combined Experience', icon: FaChartBar },
              { number: projectsCount, suffix: '+', label: 'Projects Completed', icon: FaCheckCircle },
              { number: officesCount, suffix: '', label: 'Office Locations', icon: FaBuilding },
              { number: servicesCount, suffix: '', label: 'Core Services', icon: FaBolt }
            ].map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <motion.div
                  key={index}
                  className="text-center group"
                  variants={scaleIn}
                  whileHover={{ scale: 1.1, transition: { duration: 0.3 } }}
                >
                  <div className="text-4xl mb-4 group-hover:animate-bounce text-secondary-300">
                    <IconComponent className="mx-auto" />
                  </div>
                  <div className="text-4xl lg:text-5xl font-bold text-secondary-300 mb-2">
                    {stat.number}{stat.suffix}
                  </div>
                  <div className="text-sm lg:text-base text-gray-200 font-medium">{stat.label}</div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Services Preview - Our Core Expertise */}
      <section className="py-16 lg:py-24 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeInUp}
          >
            <motion.div
              className="inline-flex items-center justify-center w-20 h-20 bg-navy-900 rounded-full mb-6"
              whileHover={{ rotate: 360, transition: { duration: 0.6 } }}
            >
              <FaLightbulb className="text-4xl text-white" />
            </motion.div>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-navy-900 dark:text-white">
              Our Core Expertise
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              We provide comprehensive consulting services across multiple sectors,
              delivering tailored solutions that drive sustainable growth and measurable impact.
            </p>
          </motion.div>

          {/* Services Grid */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={staggerContainer}
          >
            {[
              {
                title: 'Development Consulting',
                description: 'M&E frameworks, research studies, and data collection for development projects that create lasting social impact.',
                icon: FaUsers,
                services: ['Monitoring & Evaluation', 'Research & Development', 'Field Data Collection', 'Impact Assessment']
              },
              {
                title: 'Business Advisory',
                description: 'Strategic planning, investment guidance, and business optimization services for sustainable growth.',
                icon: FaBriefcase,
                services: ['Strategic Planning', 'Investment Guidance', 'Business & Tax Advisory', 'Financial Modeling']
              },
              {
                title: 'Tourism & IT Solutions',
                description: 'Tourism advisory and technology consulting for operational efficiency and digital transformation.',
                icon: FaUmbrellaBeach,
                services: ['Tourism Advisory', 'IT Consulting', 'Digital Transformation', 'Tech Solutions']
              },
              {
                title: 'Public Sector Excellence',
                description: 'Government partnerships and institutional development programs across Africa for policy impact.',
                icon: FaLandmark,
                services: ['Public Sector Consulting', 'Policy Development', 'Institutional Partnerships', 'Governance']
              },
              {
                title: 'Agriculture & Energy',
                description: 'Sustainable agriculture and energy solutions for African markets focused on climate resilience.',
                icon: FaSeedling,
                services: ['Agriculture Consulting', 'Energy & Natural Resources', 'Climate-Smart Solutions', 'Sustainability']
              },
              {
                title: 'Real Estate & Environment',
                description: 'Property consulting and environmental impact assessments for sustainable development projects.',
                icon: FaHome,
                services: ['Real Estate Consulting', 'Environmental Impact', 'Sustainability Planning', 'Property Valuation']
              }
            ].map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div
                  key={index}
                  className="group bg-gray-50 dark:bg-gray-800 rounded-xl p-6 hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 dark:border-gray-700 hover:border-navy-900 dark:hover:border-navy-900"
                  variants={scaleIn}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                >
                  {/* Icon */}
                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-navy-900 shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="text-3xl text-white" />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold mb-3 text-navy-900 dark:text-white">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Services List */}
                  <div className="space-y-2">
                    {service.services.map((item, idx) => (
                      <div key={idx} className="flex items-start text-xs text-gray-600 dark:text-gray-400">
                        <span className="w-1.5 h-1.5 bg-navy-900 dark:bg-secondary-500 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Bottom CTA */}
          <motion.div
            className="text-center mt-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Link
              to="/services"
              className="inline-flex items-center px-8 py-4 bg-navy-900 dark:bg-secondary-500 text-white rounded-full font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              View All Services
              <span className="ml-2">→</span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="py-16 lg:py-24 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-navy-900 dark:text-secondary-500">
              Our Strategic Locations
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Strategically positioned to serve clients across Tanzania and the region
            </p>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {[
              {
                city: 'Zanzibar',
                title: 'Island Operations Hub',
                description: 'Our main office located in the historic Stone Town, serving as the gateway to East Africa\'s tourism and investment opportunities.',
                address: 'P.O. BOX 229, ZANZIBAR',
                specialties: [],
                icon: FaMapMarkerAlt
              },
              {
                city: 'Dar es Salaam',
                title: 'Commercial Center',
                description: 'Our mainland office in Tanzania\'s commercial capital, focusing on large-scale consulting projects and government partnerships.',
                address: 'Commercial District, Dar es Salaam',
                specialties: [],
                icon: FaCity
              }
            ].map((location, index) => {
              const IconComponent = location.icon;
              return (
                <motion.div
                  key={index}
                  className="bg-white dark:bg-gray-700 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={index === 0 ? slideInLeft : slideInRight}
                  whileHover={{ y: -8, transition: { duration: 0.3 } }}
                >
                  <div className="p-8">
                    <div className="flex items-center mb-6">
                      <div className="text-5xl mr-4 group-hover:scale-110 transition-transform duration-300 text-navy-900 dark:text-secondary-500">
                        <IconComponent />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-navy-900 dark:text-secondary-500">{location.city}</h3>
                        <p className="text-lg text-secondary-600 dark:text-secondary-400 font-medium">{location.title}</p>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">{location.description}</p>
                    <div className="mb-6">
                      <h4 className="font-semibold text-navy-900 dark:text-secondary-500 mb-3">📍 Address:</h4>
                      <p className="text-gray-600 dark:text-gray-300">{location.address}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications & Legal Compliance Section */}
      <CertificationsSection />

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            className="max-w-4xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2
              className="text-3xl lg:text-5xl font-bold mb-6"
              variants={fadeInUp}
            >
              Ready to Transform Your Vision into Reality?
            </motion.h2>
            <motion.p
              className="text-xl lg:text-2xl text-gray-200 mb-12 leading-relaxed"
              variants={fadeInUp}
            >
              Whether you're a government ministry seeking strategic planning, a non-profit measuring impact,
              or an investor exploring opportunities in Tanzania, we're here to guide you every step of the way.
            </motion.p>
            <motion.div
              className="flex flex-col sm:flex-row gap-6 justify-center items-center"
              variants={fadeInUp}
            >
              <Link
                to="/contact"
                className="group relative bg-white text-navy-900 px-10 py-4 rounded-full font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center"
              >
          
                Consult Us Today
                <span className="ml-2">→</span>
              </Link>
              <Link
                to="/services"
                className="bg-transparent border-2 border-white text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-navy-900 transition-all duration-300 flex items-center"
              >

                Explore Our Services
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

    
    </div>
  );
};

export default Home;